import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, doc, updateDoc, arrayUnion, arrayRemove, getDoc, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FaThumbsUp, FaThumbsDown, FaEllipsisH, FaFlag, FaTrash, FaInbox, FaRegComment, FaUserCircle, FaEdit } from 'react-icons/fa';
import ShowCompletePostModal from './ShowCompletePostModal';
import { Link } from 'react-router-dom';
import ConfirmationModal from '../ConfirmationModal';
import { motion } from 'framer-motion';

function ForumMainContent({ 
  posts, 
  loading, 
  hasMore, 
  onLoadMore, 
  onPostUpdated, 
  selectedLabel,
  sortOrder,
  onSortOrderChange
}) {
  const { currentUser } = useAuth();
  const [selectedPost, setSelectedPost] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownRef = useRef(null);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [postToReport, setPostToReport] = useState(null);
  const actionRowRef = useRef(null);
  const [labelsData, setLabelsData] = useState({});

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    // Check admin status
    const checkAdminStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setIsAdmin(userDoc.data().isAdmin || false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  useEffect(() => {
    if (openDropdownId === null) return;
    function handleClickOutside(event) {
      if (actionRowRef.current && !actionRowRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  useEffect(() => {
    // Fetch labels data to display label names
    const fetchLabels = async () => {
      try {
        const labelsQuery = query(collection(db, 'forumLabels'));
        const labelsSnapshot = await getDocs(labelsQuery);
        
        const labelsMapping = {};
        labelsSnapshot.docs.forEach(doc => {
          const labelData = doc.data();
          if (labelData.labelId) {
            labelsMapping[labelData.labelId] = labelData.labelName;
          }
        });
        
        setLabelsData(labelsMapping);
      } catch (error) {
        console.error('Error fetching labels:', error);
      }
    };

    fetchLabels();
  }, []);

  const handleVote = async (postId, voteType) => {
    try {
      const postRef = doc(db, 'posts', postId);
      const postDoc = await getDoc(postRef);
      
      if (!postDoc.exists()) {
        toast.error('Post not found');
        return;
      }

      const postData = postDoc.data();
      const currentUpvotes = postData.upvoteCount || 0;
      const currentDownvotes = postData.downvoteCount || 0;
      const upvotedBy = postData.upvotedBy || [];
      const downvotedBy = postData.downvotedBy || [];

      let updates = {
        upvoteCount: currentUpvotes,
        downvoteCount: currentDownvotes,
        upvotedBy: upvotedBy,
        downvotedBy: downvotedBy
      };

      // Handle upvote
      if (voteType === 'up') {
        if (upvotedBy.includes(currentUser.uid)) {
          // User is removing their upvote
          updates.upvoteCount = currentUpvotes - 1;
          updates.upvotedBy = arrayRemove(currentUser.uid);
        } else {
          // User is adding an upvote
          updates.upvoteCount = currentUpvotes + 1;
          updates.upvotedBy = arrayUnion(currentUser.uid);
          
          // If user had previously downvoted, remove it
          if (downvotedBy.includes(currentUser.uid)) {
            updates.downvoteCount = currentDownvotes - 1;
            updates.downvotedBy = arrayRemove(currentUser.uid);
          }
        }
      }
      // Handle downvote
      else if (voteType === 'down') {
        if (downvotedBy.includes(currentUser.uid)) {
          // User is removing their downvote
          updates.downvoteCount = currentDownvotes - 1;
          updates.downvotedBy = arrayRemove(currentUser.uid);
        } else {
          // User is adding a downvote
          updates.downvoteCount = currentDownvotes + 1;
          updates.downvotedBy = arrayUnion(currentUser.uid);
          
          // If user had previously upvoted, remove it
          if (upvotedBy.includes(currentUser.uid)) {
            updates.upvoteCount = currentUpvotes - 1;
            updates.upvotedBy = arrayRemove(currentUser.uid);
          }
        }
      }

      await updateDoc(postRef, updates);
      
      // // Show appropriate message
      // if (voteType === 'up') {
      //   if (upvotedBy.includes(currentUser.uid)) {
      //     toast.success('Upvote removed');
      //   } else {
      //     toast.success('Post upvoted');
      //   }
      // } else {
      //   if (downvotedBy.includes(currentUser.uid)) {
      //     toast.success('Downvote removed');
      //   } else {
      //     toast.success('Post downvoted');
      //   }
      // }
      // Trigger parent refetch
      if (typeof onPostUpdated === 'function') {
        onPostUpdated();
      }
    } catch (error) {
      console.error('Error voting:', error);
      if (error.code === 'permission-denied') {
        toast.error('You do not have permission to vote on posts');
      } else {
        toast.error('Failed to vote on post');
      }
    }
  };

  const handleReport = async () => {
    if (!postToReport) return;
    
    try {
      const postRef = doc(db, 'posts', postToReport.id);
      await updateDoc(postRef, {
        reportedBy: arrayUnion(currentUser.uid),
        reportStatus: 'active'
      });
      toast.success('Post reported successfully');
    } catch (error) {
      console.error('Error reporting post:', error);
      toast.error('Failed to report post');
    } finally {
      setShowReportModal(false);
      setPostToReport(null);
    }
  };

  const confirmReport = (post) => {
    setPostToReport(post);
    setShowReportModal(true);
  };

  const confirmDeletePost = (post) => {
    setPostToDelete(post);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!postToDelete) return;
    
    try {
      const postRef = doc(db, 'posts', postToDelete.id);
      await updateDoc(postRef, {
        postStatus: 'removed'
      });
      toast.success('Post removed successfully');
      
      // Trigger refetch after deletion
      if (typeof onPostUpdated === 'function') {
        onPostUpdated();
      }
    } catch (error) {
      console.error('Error removing post:', error);
      toast.error('Failed to remove post');
    } finally {
      setShowDeleteModal(false);
      setPostToDelete(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = typeof timestamp.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleEditPost = async (postId) => {
    try {
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        postContent: editedContent,
        lastEditedAt: new Date()
      });
      toast.success('Post updated successfully');
      setEditingPostId(null);
      setEditedContent('');
      if (typeof onPostUpdated === 'function') {
        onPostUpdated();
      }
    } catch (error) {
      console.error('Error updating post:', error);
      toast.error('Failed to update post');
    }
  };

  const EmptyState = () => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-16 px-4 bg-white dark:bg-gray-800 rounded-xl shadow-md"
    >
      <div className="flex flex-col items-center justify-center space-y-4">
        <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-5">
          <FaInbox className="w-10 h-10 text-gray-400 dark:text-gray-500" />
        </div>
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          No Posts Yet
        </h3>
        <p className="text-gray-600 dark:text-gray-400 max-w-sm">
          {selectedLabel 
            ? `There are no posts with the selected label yet. Be the first to create one!`
            : "There are no posts yet. Be the first to create one!"}
        </p>
      </div>
    </motion.div>
  );

  return (
    <div>
      {/* Top bar of the forum */}
      <div className="flex justify-between items-center mb-4">
        {/* Reported Zone */}
        {isAdmin && (
          <div className="flex justify-between items-center mb-4">
            <Link
              to="/forum/reported"
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2 shadow-md transition-all duration-200"
            >
              <FaFlag />
              Reported Posts
            </Link>
          </div>
        )}

        {/* Sort Order Dropdown */}
        <div className="flex justify-end pb-4">
          <select
            value={sortOrder}
            onChange={(e) => onSortOrderChange(e.target.value)}
            className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="recent">Most Recent</option>
            <option value="popular">Most Popular</option>
          </select>
        </div>
      </div>
      
      {/* Posts List */}
      {loading && posts.length === 0 ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-opacity-30 border-t-purple-500 mx-auto"></div>
        </div>
      ) : posts.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-6">
          {posts.map(post => (
            <motion.div 
              key={post.id} 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
            >
              <div className="flex flex-col sm:flex-row">
                {/* Voting Buttons */}
                <div className="flex sm:flex-col items-center justify-center gap-1 p-2 sm:p-4 bg-gray-50 dark:bg-gray-700/50 sm:w-auto w-full rounded-t-xl sm:rounded-l-xl sm:rounded-t-none sm:rounded-r-none">
                  <div className="flex items-center gap-6 sm:flex-col">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVote(post.id, 'up');
                      }}
                      className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                        post.upvotedBy?.includes(currentUser.uid) ? 'text-green-500 bg-green-50 dark:bg-green-700/30' : 'text-gray-500'
                      }`}
                    >
                      <FaThumbsUp size={18} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVote(post.id, 'down');
                      }}
                      className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                        post.downvotedBy?.includes(currentUser.uid) ? 'text-red-500 bg-red-50 dark:bg-red-700/30' : 'text-gray-500'
                      }`}
                    >
                      <FaThumbsDown size={18} />
                    </button>
                  </div>
                </div>

                {/* Post Content */}
                <div 
                  className="flex-1 p-4 sm:p-5 cursor-pointer relative rounded-b-xl sm:rounded-b-none sm:rounded-r-xl sm:rounded-l-none"
                  onClick={() => {
                    if (editingPostId !== post.id) {
                      setSelectedPost(post);
                      setShowModal(true);
                    }
                  }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white hover:text-purple-500 dark:hover:text-purple-400 transition-colors flex-1 mr-2 truncate">
                      {post.postTitle}
                    </h3>
                    <div className="relative flex-shrink-0">
                      {openDropdownId === post.id ? (
                        <motion.div
                          ref={actionRowRef}
                          initial={{ opacity: 0, x: 40 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 40 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                          className="flex gap-2 items-center"
                        >
                          {(currentUser.uid === post.userId || isAdmin) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingPostId(post.id);
                                setEditedContent(post.postContent);
                                setOpenDropdownId(null);
                              }}
                              className="p-2 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors"
                              title="Edit Post"
                            >
                              <FaEdit />
                            </button>
                          )}
                          {currentUser.uid !== post.userId && !post.isAdmin && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmReport(post);
                                setOpenDropdownId(null);
                              }}
                              className="p-2 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-800 transition-colors"
                              title="Report Post"
                            >
                              <FaFlag />
                            </button>
                          )}
                          {(currentUser.uid === post.userId || isAdmin) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmDeletePost(post);
                                setOpenDropdownId(null);
                              }}
                              className="p-2 rounded-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-800 transition-colors"
                              title="Delete Post"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </motion.div>
                      ) : (
                        (currentUser.uid === post.userId || isAdmin || (!post.isAdmin && currentUser.uid !== post.userId)) && (
                          <button
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(post.id);
                            }}
                            aria-label="Show actions"
                          >
                            <FaEllipsisH className="text-gray-500" />
                          </button>
                        )
                      )}
                    </div>
                  </div>
                  
                  {editingPostId === post.id ? (
                    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                      <textarea
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent whitespace-pre-wrap"
                        rows={4}
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setEditingPostId(null);
                            setEditedContent('');
                          }}
                          className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleEditPost(post.id)}
                          className="px-4 py-2 text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative mb-4">
                      <p className="text-gray-600 dark:text-gray-400 line-clamp-3 whitespace-pre-wrap">{post.postContent}</p>
                      {post.postContent && post.postContent.length > 200 && (
                        <div className="mt-2 flex">
                          <span className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium cursor-pointer transition-colors">
                            Click to see more
                            <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Post labels */}
                  {post.postLabels && post.postLabels.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4 font-medium">
                      {post.postLabels.map(labelId => (
                        <span 
                          key={labelId}
                          className="px-2 pt-0.5 pb-1 text-xs rounded-full border bg-violet-200 text-violet-700 border-violet-200"
                        >
                          {labelsData[labelId] || labelId}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex flex-wrap items-center text-xs sm:text-sm text-gray-500 dark:text-gray-400 gap-y-1">
                    <div className="flex items-center mr-4">
                      {post?.isAdmin ? (
                        <FaUserCircle className="mr-1 sm:mr-2 text-indigo-500" size={16} />
                      ) : (
                        <FaUserCircle className="mr-1 sm:mr-2" size={16} />
                      )}
                      <span>{post.username}</span>
                      {post?.isAdmin && (
                        <motion.span 
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.3, type: "spring", stiffness: 200 }}
                          className="ml-1.5 px-2 pt-0.5 pb-1 bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 text-purple-700 dark:text-indigo-300 rounded text-xs font-medium border border-purple-200 dark:border-indigo-800/30 shadow-sm"
                        >
                          Admin
                        </motion.span>
                      )}
                    </div>
                    <div className="mr-4">{formatDate(post.createdAt)}</div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center">
                        <FaThumbsUp className="text-green-500 mr-1" />
                        <span>{post.upvoteCount || 0}</span>
                      </div>
                      <div className="flex items-center">
                        <FaThumbsDown className="text-red-500 mr-1" />
                        <span>{post.downvoteCount || 0}</span>
                      </div>
                      <div className="flex items-center">
                        <FaRegComment className="mr-1" />
                        <span>{post.commentCount || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && !loading && posts.length > 0 && (
        <div className="text-center py-4">
          <button
            onClick={onLoadMore}
            className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 shadow transition-colors"
          >
            Load More Posts
          </button>
        </div>
      )}

      {/* Post Modal */}
      <ShowCompletePostModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        post={selectedPost}
        sortOrder={sortOrder}
        onPostUpdated={onPostUpdated}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setPostToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Post"
        message={postToDelete ? `Are you sure you want to delete the post "${postToDelete.postTitle}"? This action cannot be undone.` : ''}
      />

      {/* Report Confirmation Modal */}
      <ConfirmationModal
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setPostToReport(null);
        }}
        onConfirm={handleReport}
        title="Report Post"
        message={postToReport ? `Are you sure you want to report the post "${postToReport.postTitle}"? This will notify moderators to review the content.` : ''}
        confirmText="Report"
        confirmButtonClass="bg-orange-500 hover:bg-orange-600"
      />
    </div>
  );
}

export default ForumMainContent;
